import React, { useState, useEffect } from "react";
import badgeService from "../../services/badgeService";
import {
  HiOutlineSparkles,
  HiOutlineStar,
  HiOutlineChartBar,
  HiOutlineTrophy,
  HiOutlineFlag,
  HiOutlineLockClosed,
} from "react-icons/hi2";

/**
 * Onglet des badges
 * Affiche les badges disponibles, obtenus et les prochains à débloquer
 */
const BadgesTab = ({ user, token }) => {
  const [allBadges, setAllBadges] = useState([]);
  const [userBadges, setUserBadges] = useState([]);
  const [nextBadges, setNextBadges] = useState([]);
  const [badgeStats, setBadgeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("all"); // all, my-badges, progress

  useEffect(() => {
    loadBadgesData();
  }, [user]);

  const loadBadgesData = async () => {
    setLoading(true);
    try {
      const badgesData = await badgeService.getAllBadges();
      setAllBadges(badgesData.badges || []);

      if (user) {
        const [userBadgesData, nextBadgesData, statsData] = await Promise.all([
          badgeService.getUserBadges(user.id),
          badgeService.getNextBadges(),
          badgeService.getUserBadgeStats(user.id),
        ]);

        setUserBadges(userBadgesData.badges || []);
        setNextBadges(nextBadgesData.nextBadges || []);
        setBadgeStats(statsData);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des badges:", error);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeColor = (color) => {
    const colors = {
      bronze: "from-orange-400 to-orange-600",
      silver: "from-gray-300 to-gray-500",
      gold: "from-yellow-400 to-yellow-600",
      platinum: "from-cyan-400 to-blue-600",
      diamond: "from-purple-400 to-pink-600",
    };
    return colors[color] || "from-gray-400 to-gray-600";
  };

  const hasBadge = (badgeId) => {
    return userBadges.some((b) => b.id === badgeId);
  };

  const renderBadgeCard = (badge, earned = false, progress = null) => {
    return (
      <div
        key={badge.id}
        className={`bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border-2 transition-all duration-200 hover:shadow-md ${
          earned ? "border-ink-900/20 dark:border-white/30" : "border-ink-900/10 dark:border-white/20 opacity-80"
        }`}
      >
        <div className="p-4 sm:p-6">
          {/* Badge icon et nom - stack sur mobile */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div
              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center text-2xl sm:text-3xl shadow-lg flex-shrink-0 ${
                earned
                  ? `bg-gradient-to-br ${getBadgeColor(badge.color)}`
                  : "bg-ink-100 dark:bg-white/20"
              }`}
            >
              {earned ? (
                typeof badge.icon === "string" ? (
                  <span className="text-3xl">{badge.icon}</span>
                ) : (
                  badge.icon
                )
              ) : (
                <HiOutlineLockClosed className="w-8 h-8 text-ink-400 dark:text-white/70" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-ink-900 dark:text-white mb-1 break-words">
                {badge.name}
              </h3>
              <p className="text-xs sm:text-sm text-ink-600 dark:text-white/90 line-clamp-2">{badge.description}</p>
            </div>
          </div>

          {/* Niveau et couleur */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold ${
                earned
                  ? `text-white bg-gradient-to-r ${getBadgeColor(badge.color)}`
                  : "text-ink-700 dark:text-white bg-ink-200 dark:bg-white/30"
              }`}
            >
              Niveau {badge.level}
            </span>

            {earned && badge.earned_at && (
              <span className="text-xs text-ink-500 dark:text-white/80">
                Obtenu le{" "}
                {new Date(badge.earned_at).toLocaleDateString("fr-FR")}
              </span>
            )}
          </div>

          {/* Barre de progression pour les badges non obtenus */}
          {!earned && progress !== null && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-ink-700 dark:text-white/90 font-medium">Progression</span>
                <span className="font-bold text-orange-300">
                  {progress.current_progress || 0} / {progress.target}
                </span>
              </div>
              <div className="w-full bg-ink-200 dark:bg-white/20 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-orange-500 to-amber-500 h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      ((progress.current_progress || 0) / progress.target) *
                        100,
                      100,
                    )}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-ink-500 dark:text-white/80 mt-2">
                Encore{" "}
                {Math.max(
                  progress.target - (progress.current_progress || 0),
                  0,
                )}{" "}
                pour débloquer
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 sm:py-20">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-3 sm:mt-4 text-ink-700 dark:text-white text-sm sm:text-base">Chargement des badges...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Statistiques des badges (si connecté) */}
      {user && badgeStats && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 text-white">
          <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
            <HiOutlineSparkles className="w-6 h-6 sm:w-7 sm:h-7 shrink-0" />
            <span>Vos Badges</span>
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold tabular-nums">{badgeStats.total_badges || 0}</div>
              <div className="text-xs sm:text-sm opacity-90">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold tabular-nums">{badgeStats.bronze_count || 0}</div>
              <div className="text-xs sm:text-sm opacity-90">Bronze</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold tabular-nums">{badgeStats.silver_count || 0}</div>
              <div className="text-xs sm:text-sm opacity-90">Argent</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold tabular-nums">{badgeStats.gold_count || 0}</div>
              <div className="text-xs sm:text-sm opacity-90">Or</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold tabular-nums">{badgeStats.highest_level || 0}</div>
              <div className="text-xs sm:text-sm opacity-90">Niveau Max</div>
            </div>
          </div>
        </div>
      )}

      {/* Sélecteur de vue - stack sur mobile, row sur desktop */}
      {user && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-ink-100 dark:bg-white/10 backdrop-blur-sm rounded-xl p-1.5 sm:p-1 border border-ink-900/10 dark:border-white/20">
          <button
            onClick={() => setActiveView("all")}
            className={`flex-1 min-w-0 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all duration-200 touch-manipulation flex items-center justify-center gap-1.5 sm:gap-2 ${
              activeView === "all"
                ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md"
                : "text-ink-700 hover:bg-ink-100 dark:text-white dark:hover:bg-white/20"
            }`}
          >
            <HiOutlineSparkles className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            <span className="truncate">Tous ({allBadges.length})</span>
          </button>
          <button
            onClick={() => setActiveView("my-badges")}
            className={`flex-1 min-w-0 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all duration-200 touch-manipulation flex items-center justify-center gap-1.5 sm:gap-2 ${
              activeView === "my-badges"
                ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md"
                : "text-ink-700 hover:bg-ink-100 dark:text-white dark:hover:bg-white/20"
            }`}
          >
            <HiOutlineStar className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            <span className="truncate">Mes badges ({userBadges.length})</span>
          </button>
          <button
            onClick={() => setActiveView("progress")}
            className={`flex-1 min-w-0 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all duration-200 touch-manipulation flex items-center justify-center gap-1.5 sm:gap-2 ${
              activeView === "progress"
                ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md"
                : "text-ink-700 hover:bg-ink-100 dark:text-white dark:hover:bg-white/20"
            }`}
          >
            <HiOutlineChartBar className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            <span className="truncate">Progression ({nextBadges.length})</span>
          </button>
        </div>
      )}

      {/* Contenu */}
      {activeView === "all" && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2 mb-4 sm:mb-6">
            <HiOutlineSparkles className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400/90 shrink-0" />
            <span className="break-words">Tous les Badges Disponibles</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {allBadges.map((badge) =>
              renderBadgeCard(badge, user && hasBadge(badge.id)),
            )}
          </div>
        </div>
      )}

      {activeView === "my-badges" && user && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2 mb-4 sm:mb-6">
            <HiOutlineStar className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400/90 shrink-0" />
            <span>Mes Badges Obtenus</span>
          </h2>

          {userBadges.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {userBadges.map((badge) => renderBadgeCard(badge, true))}
            </div>
          ) : (
            <div className="text-center py-10 sm:py-16 bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 px-4">
              <HiOutlineFlag className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-ink-400 dark:text-white/60" />
              <h3 className="text-lg sm:text-xl font-bold text-ink-900 dark:text-white mb-2">
                Aucun badge pour le moment
              </h3>
              <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base">
                Commencez à partager des recettes pour gagner des badges !
              </p>
            </div>
          )}
        </div>
      )}

      {activeView === "progress" && user && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2 mb-4 sm:mb-6">
            <HiOutlineChartBar className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400/90 shrink-0" />
            <span className="break-words">Prochains Badges à Débloquer</span>
          </h2>

          {nextBadges.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {nextBadges.map((badge) => renderBadgeCard(badge, false, badge))}
            </div>
          ) : (
            <div className="text-center py-10 sm:py-16 bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 px-4">
              <HiOutlineTrophy className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-amber-400" />
              <h3 className="text-lg sm:text-xl font-bold text-ink-900 dark:text-white mb-2">
                Félicitations !
              </h3>
              <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base">
                Vous avez débloqué tous les badges disponibles !
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BadgesTab;
